import { useEffect, useRef, useState } from "react";

import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { finishBrowserLogin, startBrowserLogin, type BrowserLogin } from "@/boot/auth";
import { BOT_USERNAME } from "@/lib/env";

interface Props {
  onAuthed: () => void;
}

const linkStyle = "inline-flex min-h-touch items-center justify-center rounded-card px-4 py-3 text-accent underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent";

export function SplashLoginView({ onAuthed }: Props) {
  const [login, setLogin] = useState<BrowserLogin | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [ended, setEnded] = useState(false);
  const checking = useRef(false);

  // PC audit 28.09, point 14: after «Подтвердить вход» in the bot the page signs in
  // by itself — it asks every few seconds and when the tab comes back into view.
  // A pending answer changes nothing on the server, so asking often is safe.
  useEffect(() => {
    if (!login || ended) return;
    let stopped = false;
    const check = async () => {
      if (checking.current || stopped) return;
      checking.current = true;
      try {
        const result = await finishBrowserLogin(login);
        if (stopped) return;
        if (result === "authenticated") onAuthed();
        else if (result !== "pending") {
          setEnded(true);
          setMessage(result === "denied" ? "Вход отклонён в Telegram. Можно начать заново." : "Время на подтверждение истекло. Начни вход заново.");
        }
      } catch {
        // The button below still works; a failed quiet check says nothing.
      } finally {
        checking.current = false;
      }
    };
    const timer = window.setInterval(() => void check(), 3000);
    const onVisible = () => { if (document.visibilityState === "visible") void check(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { stopped = true; window.clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, [login, ended, onAuthed]);

  async function start() {
    setBusy(true);
    setMessage("");
    try {
      const next = await startBrowserLogin();
      setLogin(next);
      setEnded(false);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Не удалось начать вход.");
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    if (!login) return;
    setBusy(true);
    setMessage("");
    try {
      const result = await finishBrowserLogin(login);
      if (result === "authenticated") {
        onAuthed();
        return;
      }
      setEnded(result !== "pending");
      setMessage(result === "pending"
        ? "Сначала нажми «Подтвердить вход» в личном чате бота, затем вернись сюда."
        : result === "denied" ? "Вход отклонён в Telegram. Можно начать заново."
        : "Время на подтверждение истекло. Начни вход заново.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Не удалось проверить вход.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-app flex-col items-center justify-center gap-6 p-6 text-center">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold">prosto.task</h1>
        <p className="text-base text-hint">Твоя рабочая область</p>
      </header>
      <Card className="w-full space-y-4">
        <h2 className="text-lg font-semibold">Вход через Telegram</h2>
        {!login ? (
          <>
            <p>Подтверди вход в личном чате бота. Пароль и копирование токена не нужны.</p>
            <Button block loading={busy} loadingText="Готовим вход…" onClick={() => void start()}>
              Войти через Telegram
            </Button>
          </>
        ) : (
          <>
            <p>Открой бота и сверь код с сообщением в Telegram:</p>
            <p className="text-3xl font-semibold tracking-widest" aria-label={`Код подтверждения ${login.pair_code}`}>
              {login.pair_code}
            </p>
            {!ended && (
              <>
                <a className="inline-flex min-h-touch w-full items-center justify-center rounded-card bg-btn px-4 py-3 text-btntext focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent" href={login.bot_url}
                  target="_blank" rel="noopener noreferrer">Открыть бота для подтверждения</a>
                <p className="text-sm text-hint">Нажми «Подтвердить вход» в боте — страница войдёт сама.</p>
                <Button block loading={busy} loadingText="Проверяем вход…" onClick={() => void finish()}>
                  Продолжить на сайте
                </Button>
                <p className="text-sm text-hint">Запрос действует 10 минут. Подтвердить можно с телефона.</p>
              </>
            )}
            <Button variant="ghost" block disabled={busy} onClick={() => void start()}>
              Начать заново
            </Button>
          </>
        )}
        {message && <p role="status" aria-live="polite" className="text-sm">{message}</p>}
      </Card>
      <div className="flex w-full flex-col items-center gap-1">
        <a className={linkStyle} href="/api/v1/agent/connect">Подключить Codex или Claude Code</a>
        {BOT_USERNAME && <a className={linkStyle} href={`https://t.me/${BOT_USERNAME}`}
          target="_blank" rel="noopener noreferrer">Открыть Telegram-бота</a>}
        <p className="text-sm text-hint">Mini App доступно в боте по кнопке «Рабочая область».</p>
      </div>
    </main>
  );
}
